import { isScheduleBlocksEnabled } from "@/features/calendar/utils/utils";
import { FilterListContainer } from "@/features/projects/filter/FilterListContainer";
import { type CalendarFilters } from "@repo/api";
import { type ParentTaskType, useTeam } from "@repo/api";
import {
  Accordion,
  AccordionItem,
  Button,
  ListView,
  ListViewItem,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  FilterCheckbox,
  ModalSheet,
  type SubTaskConfig,
  type TaskConfig,
  getScheduleIcon,
  getTaskIcon,
  translateScheduleShiftType,
  translateTaskType,
  useSettings,
} from "@repo/ui";
import { SelectAllCheckboxGroup } from "@repo/ui";

import { filterTasksForCalendar } from "./utils";

interface CalendarFilterModalProps {
  isOpen: boolean;
  closeModal: () => void;
  filters: CalendarFilters;
  onFilteredTaskChange: (filteredTask: string) => void;
  onHideCanceledEventsChange: (hide: boolean) => void;
  onHideCompletedEventsChange: (hide: boolean) => void;
  onFilteredScheduleTypesChange: (scheduleType: string) => void;
  availableScheduleShiftTypes: string[];
  onHideScheduleBlocksChange: (hide: boolean) => void;
  onHideHiddenActivityChange: (hide: boolean) => void;
  selectedTeam?: number;
}

export const CalendarFilterModal = ({
  isOpen,
  closeModal,
  filters,
  onFilteredTaskChange,
  onHideCanceledEventsChange,
  onHideCompletedEventsChange,
  onHideScheduleBlocksChange,
  onHideHiddenActivityChange,
  onFilteredScheduleTypesChange,
  availableScheduleShiftTypes,
  selectedTeam,
}: CalendarFilterModalProps) => {
  const {
    filteredTasks,
    hideCanceledEvents,
    hideCompletedEvents,
    hideScheduleBlocks,
    hideHiddenActivity,
    filteredScheduleTypes,
  } = filters;

  const { task_configuration, language, calendar } = useSettings();
  const { data: team } = useTeam(selectedTeam);
  const filteredTasksForFilter: TaskConfig[] = filterTasksForCalendar(
    task_configuration?.task_types || []
  );

  const showScheduleBlocksSetting = isScheduleBlocksEnabled(
    calendar,
    team?.buCode
  );

  if (!filteredTasksForFilter) {
    return null;
  }
  const getSubtitle = (
    subTypes: SubTaskConfig[] = [],
    parentKey: ParentTaskType
  ): string => {
    const subTypeKeys = subTypes.map((subType) => subType.name_key);
    const selectedSubTypeKeys = subTypeKeys.filter((subTypeKey) =>
      filteredTasks.some((filter) => {
        const [filterParentKey, selectedSubTypeKey] = filter.split("_");
        return (
          filterParentKey === parentKey && selectedSubTypeKey === subTypeKey
        );
      })
    );

    const translatedSubTypes = selectedSubTypeKeys.map((subTypeKey) =>
      translateTaskType(
        language,
        parentKey,
        subTypeKey,
        task_configuration?.task_types
      )
    );

    return [...new Set(translatedSubTypes)].join(", ");
  };

  const handleSelectAll = (parentKey: string, isChecked: boolean) => {
    const taskItem = filteredTasksForFilter.find(
      (item) => item.name_key === parentKey
    );
    if (!taskItem?.subTypes) {
      return;
    }

    taskItem.subTypes.forEach((subType) => {
      const filterKey = `${parentKey}_${subType.name_key}`;
      if (filteredTasks.includes(filterKey) !== isChecked) {
        onFilteredTaskChange(filterKey);
      }
    });
  };

  const isSubTypeSelected = (parentKey: string, subKey: string) =>
    filteredTasks.some((filter) => {
      const [filterParentKey, filterSubKey] = filter.split("_");
      return filterParentKey === parentKey && filterSubKey === subKey;
    });

  const handleSelectAllScheduleTypes = (isChecked: boolean) => {
    if (isChecked) {
      availableScheduleShiftTypes.forEach((shiftType) => {
        if (!filteredScheduleTypes.includes(shiftType)) {
          onFilteredScheduleTypesChange(shiftType);
        }
      });
    } else {
      filteredScheduleTypes.forEach((shiftType) => {
        onFilteredScheduleTypesChange(shiftType);
      });
    }
  };

  const handleClearAll = () => {
    filteredTasks.forEach((task) => {
      onFilteredTaskChange(task);
    });

    filteredScheduleTypes.forEach((scheduleType) => {
      onFilteredScheduleTypesChange(scheduleType);
    });

    if (hideCanceledEvents) {
      onHideCanceledEventsChange(false);
    }

    if (hideCompletedEvents) {
      onHideCompletedEventsChange(false);
    }

    if (hideScheduleBlocks) {
      onHideScheduleBlocksChange(false);
    }

    if (hideHiddenActivity) {
      onHideHiddenActivityChange(false);
    }
  };

  return (
    <ModalSheet
      title={t("label.calendar_filter")}
      visible={isOpen}
      onClose={closeModal}
      footer={
        <Button
          text={t("action.clear_all")}
          size="small"
          type="primary"
          onClick={handleClearAll}
        />
      }
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ flex: 1, overflowY: "auto" }}>
          <ListView
            id="calendar-filter-toggles"
            showDivider={false}
            size="small"
          >
            <ListViewItem
              title={t("action.hide_canceled_events")}
              controlProps={{
                value: "hideCanceledEvents",
                checked: hideCanceledEvents,
              }}
              control="switch"
              onChange={() => {
                onHideCanceledEventsChange(!hideCanceledEvents);
              }}
            />
            <ListViewItem
              title={t("action.hide_completed_events")}
              controlProps={{
                value: "hideCompletedEvents",
                checked: hideCompletedEvents,
              }}
              control="switch"
              onChange={() => {
                onHideCompletedEventsChange(!hideCompletedEvents);
              }}
            />
            {showScheduleBlocksSetting && (
              <ListViewItem
                title={t("action.hide_schedule_blocks")}
                controlProps={{
                  value: "hideScheduleBlocks",
                  checked: hideScheduleBlocks,
                }}
                control="switch"
                onChange={() => {
                  onHideScheduleBlocksChange(!hideScheduleBlocks);
                }}
              />
            )}
            {showScheduleBlocksSetting && (
              <ListViewItem
                title={t("action.hide_hidden_activity")}
                controlProps={{
                  value: "hideHiddenActivity",
                  checked: hideHiddenActivity,
                }}
                control="switch"
                onChange={() => {
                  onHideHiddenActivityChange(!hideHiddenActivity);
                }}
              />
            )}
          </ListView>

          {filteredTasksForFilter.map((item) => (
            <Accordion key={item.name_key}>
              <AccordionItem
                id={`${item.name_key}-filter`}
                title={translateTaskType(
                  language,
                  item.name_key,
                  undefined,
                  task_configuration?.task_types
                )}
                subtitle={getSubtitle(item.subTypes, item.name_key)}
                open={false}
              >
                <FilterListContainer id={`${item.name_key}-filter-list`}>
                  <SelectAllCheckboxGroup
                    parentKey={item.name_key}
                    subTypes={item.subTypes || []}
                    selected={filteredTasks}
                    onSelectAll={(isChecked) =>
                      handleSelectAll(item.name_key, isChecked)
                    }
                  />

                  {item.subTypes?.map((subType) => (
                    <FilterCheckbox
                      key={`${item.name_key}-${subType.name_key}`}
                      id={subType.name_key}
                      value={subType.name_key}
                      label={translateTaskType(
                        language,
                        item.name_key,
                        subType.name_key,
                        task_configuration?.task_types
                      )}
                      checked={isSubTypeSelected(
                        item.name_key,
                        subType.name_key
                      )}
                      onCheckFilter={(filter: string) => {
                        onFilteredTaskChange(`${item.name_key}_${filter}`);
                      }}
                      icon={getTaskIcon(
                        item.name_key,
                        subType.name_key,
                        task_configuration?.task_types
                      )}
                    />
                  ))}
                </FilterListContainer>
              </AccordionItem>
            </Accordion>
          ))}
          {availableScheduleShiftTypes.length > 0 && (
            <Accordion>
              <AccordionItem
                id="schedule-types-filter"
                title={t("label.schedule_types")}
                subtitle={(filters.filteredScheduleTypes ?? [])
                  .map((s) => translateScheduleShiftType(s))
                  .join(", ")}
                open={false}
              >
                <FilterListContainer id="schedule-types-filter-list">
                  <SelectAllCheckboxGroup
                    parentKey="schedule_types"
                    subTypes={availableScheduleShiftTypes.map((type) => ({
                      name_key: type,
                    }))}
                    selected={filteredScheduleTypes.map(
                      (type) => `schedule_types_${type}`
                    )}
                    onSelectAll={(isChecked) =>
                      handleSelectAllScheduleTypes(isChecked)
                    }
                  />

                  {availableScheduleShiftTypes.map((shiftType) => (
                    <FilterCheckbox
                      key={shiftType}
                      id={shiftType}
                      value={shiftType}
                      label={translateScheduleShiftType(shiftType)}
                      checked={filteredScheduleTypes.includes(shiftType)}
                      onCheckFilter={(value: string) => {
                        onFilteredScheduleTypesChange(value);
                      }}
                      icon={getScheduleIcon(shiftType)}
                    />
                  ))}
                </FilterListContainer>
              </AccordionItem>
            </Accordion>
          )}
        </div>
      </div>
    </ModalSheet>
  );
};
